/**
 * The data hooks every feature composes from.
 *
 * Thin by design: React Query owns caching and retry policy, so these exist to
 * name the query keys and shapes in one place instead of scattering key strings
 * through components.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { connectorsApi } from '@/core/api/connectors.api'
import { connectionsApi } from '@/core/api/connections.api'
import { actionsApi } from '@/core/api/actions.api'
import { executionsApi, type ExecutionFilters } from '@/core/api/executions.api'
import { invalidateConnections, queryClient, queryKeys } from '@/lib/query.client'
import { connect, type ConnectRequest } from '@/core/auth/auth.flow.manager'
import type { ExecutionResult } from '@/core/types/api.types'

export function useConnectors(params?: { category?: string; q?: string }) {
  return useQuery({
    queryKey: queryKeys.catalog(params),
    queryFn: () => connectorsApi.catalog(params),
    // The catalog is fixed per deploy; there is nothing to poll for.
    staleTime: 15 * 60_000,
  })
}

export function useManifest(connectorId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.connector(connectorId ?? ''),
    enabled: Boolean(connectorId),
    staleTime: 15 * 60_000,
    queryFn: () => connectorsApi.detail(connectorId!),
  })
}

export function useActionSchema(
  connectorId: string | null | undefined,
  actionId: string | null | undefined,
) {
  return useQuery({
    queryKey: queryKeys.actionSchema(connectorId ?? '', actionId ?? ''),
    enabled: Boolean(connectorId && actionId),
    staleTime: 15 * 60_000,
    queryFn: () => connectorsApi.actionSchema(connectorId!, actionId!),
  })
}

/** Drives a connect flow end to end and returns the new connection. */
export function useAuthFlow() {
  return useMutation({
    mutationFn: (request: ConnectRequest) => connect(request),
    onSuccess: async () => {
      await invalidateConnections()
    },
  })
}

export function useExecuteAction(connectionId: string | null, actionId: string | null) {
  return useMutation<ExecutionResult, unknown, Record<string, unknown>>({
    mutationFn: (input) => {
      if (!connectionId || !actionId) {
        throw new Error('Choose a connection and an action first.')
      }
      return actionsApi.execute(connectionId, actionId, input)
    },
    onSettled: () => {
      // A run always produces a history row, success or failure.
      queryClient.invalidateQueries({ queryKey: ['executions'] })
    },
  })
}

export function useExecutionHistory(filters: ExecutionFilters = {}) {
  return useQuery({
    queryKey: queryKeys.executions(filters),
    queryFn: () => executionsApi.list(filters),
    staleTime: 10_000,
  })
}

export function useExecution(executionId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.execution(executionId ?? ''),
    enabled: Boolean(executionId),
    queryFn: () => executionsApi.detail(executionId!),
  })
}

export function useWhoAmI() {
  return useQuery({
    queryKey: queryKeys.whoami,
    queryFn: () => connectionsApi.whoami(),
    staleTime: Infinity,
    retry: false,
  })
}
